import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface FenetreDefilable {
  requestAnimationFrame(cb: FrameRequestCallback): number;
  scrollBy(x: number, y: number): void;
}

/**
 * Filet contre l'écran blanc durable d'iOS 27 (fiche Notion 3e36f8a6).
 *
 * Mesuré le 2026-09-28 sur l'iPhone de Thierry (inspecteur Safari) : le DOM de
 * la page est complet, mis en page et touchable, mais la vue web ne le peint
 * pas ; `scrollBy(0, 1)` dans la console le fait réapparaître aussitôt. Un
 * défilement réel au doigt aussi. La cause est dans iOS 27 — un bogue proche,
 * lié à l'effet de bord de défilement, est reconnu par Apple (forum
 * développeurs, fil 845744) ; le correctif natif masque cet effet
 * (`SceneDelegate.swift`). Ce filet rejoue le seul geste dont on a prouvé
 * qu'il débloque l'écran.
 *
 * Aller-retour d'un pixel sur deux images successives : dans la même image,
 * les deux défilements s'annuleraient avant que la vue web ne voie bouger sa
 * zone visible. Sans effet sur une page qui ne défile pas (hauteur ≤ écran) —
 * limite connue.
 */
export function relancerPeinture(fenetre: FenetreDefilable = window): void {
  fenetre.requestAnimationFrame(() => {
    fenetre.scrollBy(0, 1);
    fenetre.requestAnimationFrame(() => {
      fenetre.scrollBy(0, -1);
    });
  });
}

/**
 * Sentinelle montée une fois dans les routes (à côté de `useCascadeEntree`) :
 * relance la peinture après chaque navigation et à chaque retour de l'app au
 * premier plan. Natif seulement : le navigateur n'a pas ce défaut.
 */
export function useRelancePeinture(): void {
  const { key } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` est le déclencheur voulu — une relance par entrée d'historique.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    relancerPeinture();
  }, [key]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const handle = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) relancerPeinture();
    });
    return () => {
      void handle.then((h) => h.remove());
    };
  }, []);
}
